'use client'

import { QRCodeSVG } from 'qrcode.react'
import Link from 'next/link'
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from './dialog'

type QRCodeGreenProps = {
  /** What the QR encodes — typically the signed check-in token. */
  value: string
  /** When set, the wrapper becomes a link to the event's ticket page. */
  eventId?: string
  size?: number
  caption?: string | null
  /**
   * #1702: tapping the code opens it full-screen on white, so a member at
   * the door can hold up a code that fills the screen. Ignored when
   * `eventId` makes the wrapper a link.
   */
  enlargeable?: boolean
}

/**
 * Minimum edge, in CSS px, for a check-in QR someone is expected to scan
 * (#1702). The signed token is ~97 characters, a version 6 code: 41 modules,
 * 49 with the margin. 220 px keeps ~4.5 px per module even at a device pixel
 * ratio of 1 (a laptop screen); 88 px gave ~1.8 and did not scan off a monitor.
 */
export const CHECK_IN_QR_SIZE = 220

export function QRCodeGreen({
  value,
  eventId,
  size = 128,
  caption = 'Show this QR code to the host to check you in.',
  enlargeable = false,
}: QRCodeGreenProps) {
  const interactiveClasses = eventId
    ? ' cursor-pointer hover:bg-[#75A096]/10 transition-colors'
    : ''

  const content = (
    <div
      className={`flex flex-col items-center justify-center p-3 bg-white rounded-lg border-2 border-[#75A096]${interactiveClasses}`}
    >
      {/* The green identity lives in the BORDER + caption, never the modules:
          QR readers need dark-on-light with a quiet zone. The old orange
          (#DE866D) modules on a transparent/tinted field were unscannable —
          a solid white background at level M with margin is what a phone
          camera can actually decode. */}
      <QRCodeSVG
        value={value}
        size={size}
        level="M"
        marginSize={4}
        fgColor="#14231F"
        bgColor="#FFFFFF"
        className="mb-2 rounded-sm"
      />
      {caption ? (
        <p className="text-xs text-[#3D6A5E] text-center font-medium">{caption}</p>
      ) : null}
    </div>
  )

  if (eventId) {
    return <Link href={`/events/${eventId}/ticket`}>{content}</Link>
  }
  if (enlargeable) {
    return (
      <Dialog>
        <DialogTrigger asChild>
          <button
            type="button"
            aria-label="Show the check-in QR code full screen"
            className="block rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#75A096]"
          >
            {content}
          </button>
        </DialogTrigger>
        <DialogContent className="bg-white p-4 sm:max-w-md">
          <DialogTitle className="text-center text-base text-[#3D6A5E]">
            Show this to the host
          </DialogTitle>
          <DialogDescription className="sr-only">
            Your check-in QR code, shown full screen for the host's scanner.
          </DialogDescription>
          {/* Fills the dialog width; the SVG scales without losing sharpness. */}
          <QRCodeSVG
            value={value}
            size={360}
            level="M"
            marginSize={4}
            fgColor="#14231F"
            bgColor="#FFFFFF"
            className="h-auto w-full"
            data-testid="check-in-qr-fullscreen"
          />
        </DialogContent>
      </Dialog>
    )
  }
  return content
}
